import type { Reglement } from '../types';

/**
 * Regles d'ecran portant sur un reglement.
 *
 * Elles vivent dans un fichier a part parce que DEUX ecrans les posent —
 * l'onglet Reglements et la fiche adherent — et que deux copies divergent
 * toujours. Le serveur les tient de son cote (routes/reglements.routes) ;
 * ceci n'en est que le reflet, jamais la source.
 */

/**
 * Un reglement est-il modifiable ? La regle vit ICI et pas dans un
 * composant : deux ecrans la posent — l'onglet Reglements et la fiche
 * adherent —, et deux copies divergent toujours. Le serveur la tient de
 * son cote ; ceci n'est que son reflet.
 *
 * Un cran plus permissif que la suppression sur les remises : c'est la
 * remise DEPOSEE qui verrouille (RG-19), le bordereau etant parti en
 * banque ; une remise en preparation se retouche encore.
 *
 * Au-dela de `prevu`, retoucher un encaissement fait diverger
 * l'application de la banque : permission dediee, que seul le super admin
 * detient. A SAVOIR : un virement est `encaisse` DES SA SAISIE — le
 * secretariat ne peut donc pas y corriger une faute de frappe, consequence
 * assumee de la decision du 2026-08-28.
 */
export function peutModifierReglement(
  reglement: Reglement,
  peut: (...permissions: string[]) => boolean,
): boolean {
  if (!peut('reglement.modifier')) return false;
  // Un remboursement (montant negatif, 2026-10-09) ne se retouche pas : il
  // se supprime tant qu'il est prevu, et se ressaisit. Le serveur le refuse.
  if (reglement.montant < 0) return false;
  // Une remise VALIDEE fige sa composition, une remise DEPOSEE fige tout :
  // dans les deux cas le bordereau est arrete.
  if (reglement.remise_statut === 'validee' || reglement.remise_statut === 'deposee') return false;
  // « en cours » compte comme « prevu » : tant que le cheque n'est pas
  // parti en banque, rien n'est constate et la Direction corrige. C'est
  // ce qui rend utilisable la regle « changer la date d'encaissement sort
  // le cheque de sa remise ».
  if (reglement.statut === 'prevu' || reglement.statut === 'en_cours') return true;
  return peut('reglement.modifier_encaisse');
}

/**
 * POURQUOI un reglement ne se modifie pas — la phrase que le formulaire
 * affiche en lecture seule (2026-10-07). Null quand il se modifie.
 *
 * Un reglement verrouille s'OUVRE desormais : la Direction doit pouvoir
 * lire le detail d'un virement ou d'un cheque encaisse — sa ventilation,
 * son emetteur, son commentaire — sans pouvoir y toucher. Avant, la ligne
 * n'etait pas cliquable du tout, et le detail n'existait nulle part.
 */
export function motifLectureSeule(
  reglement: Reglement,
  peut: (...permissions: string[]) => boolean,
): string | null {
  if (peutModifierReglement(reglement, peut)) return null;
  if (!peut('reglement.modifier')) return 'Vous n’avez pas la permission de modifier un règlement.';
  if (reglement.montant < 0) {
    return 'Un remboursement ne se modifie pas : supprimez-le tant qu’il est prévu, puis ressaisissez-le.';
  }
  if (reglement.remise_statut === 'deposee') {
    return `Ce règlement fait partie de la remise ${reglement.remise_reference ?? ''}, déposée en banque : il ne se modifie plus (RG-19).`;
  }
  if (reglement.remise_statut === 'validee') {
    return `Ce règlement fait partie de la remise ${reglement.remise_reference ?? ''}, validée : sa composition est arrêtée.`;
  }
  return 'Ce règlement est encaissé : le modifier ferait diverger l’application de la banque. Seule une permission dédiée le permet.';
}
